<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    ref="scroller"
    @scroll="scrollPosition = ($event.target as HTMLElement).scrollLeft"
    class="
      flex flex-row flex-nowrap
      no-scrollbar
      overflow-x-auto overflow-y-hidden
      p-px snap-x snap-mandatory scroll-px-px
      min-w-full w-full
    "
  >
    <div
      v-for="(instructor, index) in instructors"
      :data-index="index"
      data-instructor="1"
      class="
        min-w-[calc(100%+0.5px)] w-full
        sm:min-w-[calc(50%+0.125px)] sm:w-1/2
        lg:min-w-[calc(25%+0.125px)] lg:w-1/4
        overflow-y-hidden
        ring-1 ring-solid ring-gray-100
        bg-white
        snap-start
      "
      :class="{
        'fade-in-section delayed': shouldFadeIn(index)
      }"
      :style="shouldFadeIn(index) ? `--fade-in-delay: ${ index+1 }00ms;` : null"
    >
      <div
        data-id="card-block"
        class="
          block h-full group rounded-none
          no-underline text-black
        "
      >
        <div class="h-full flex flex-col">
          <FocalImage
            v-if="instructor.image"
            :image="{ url: instructor.image, alt: instructor.name }"
            img-class="w-full relative left-0 h-auto aspect-video"
          />
          <div class="h-full flex flex-col p-8">
            <span class="text-2xl font-semibold">
              <a
                :href="instructor.url"
                class="text-gray-800 block underline hover:text-red-600 focus:text-red-600 mb-2"
                tabindex="0"
              >
                {{ instructor.name }}
              </a>
            </span>
            <div class="body-2">
              <div
                class="mb-3"
                v-html="instructor.bio"
              ></div>
              <template v-if="instructor.topics.length > 0">
                <p class="font-bold">Topics taught:</p>
                <span>
                  <template v-for="(topic, i) in instructor.topics">
                    <a
                      :href="`/training/?facet_sei_tags=${encodeURIComponent(topic.name)}#course_list`"
                      class="inline"
                      tabindex="0"
                    >{{ topic.name }}</a><template v-if="i !== instructor.topics.length-1 && instructor.topics.length > 1">, </template>
                  </template>
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="mt-6 flex justify-end gap-2">
    <button
      class="
        block w-fit px-2 py-2
        border bg-white
      "
      :class="{
        'border-red-100 text-red-600 hover:bg-red-50 hover:text-red-700 cursor-pointer': scrollPosition > 1,
        'border-gray-600 text-gray-600 opacity-25 cursor-default': scrollPosition <= 1
      }"
      :tabindex="scrollPosition > 1 ? 0 : -1"
      @click.prevent="scrollPosition > 1 ? scrollLeft() : null"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
      >
        <path
          fill="currentColor"
          d="m14 18l-6-6l6-6l1.4 1.4l-4.6 4.6l4.6 4.6z"
        />
      </svg>
      <span class="sr-only">Previous</span>
    </button>
    <div
      class="flex flex-col justify-center"
    >
      <div>
        <span>{{ focusedIndex() }}</span>
        <span>/</span>
        <span data-id="size" data-value="1" class="inline-block sm:hidden">{{ instructors.length }}</span>
        <span data-id="size" data-value="2" class="hidden sm:inline-block lg:hidden">{{ Math.ceil(instructors.length/2) }}</span>
        <span data-id="size" data-value="4" class="hidden lg:inline-block">{{ Math.ceil(instructors.length/4) }}</span>
      </div>
    </div>
    <button
      class="block w-fit px-2 py-2 border bg-white"
      :class="{
        'border-red-100 text-red-600 hover:bg-red-50 hover:text-red-700 cursor-pointer': scrollPosition < scrollMax(),
        'border-gray-600 text-gray-600 opacity-25 cursor-default': scrollPosition >= scrollMax()
      }"
      :tabindex="scrollPosition < scrollMax() ? 0 : -1"
      @click.prevent="scrollPosition < scrollMax() ? scrollRight() : null"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
      >
        <path
          fill="currentColor"
          d="M12.6 12L8 7.4L9.4 6l6 6l-6 6L8 16.6z"
        />
      </svg>
      <span class="sr-only">Next</span>
    </button>
  </div>
</template>

<script setup lang="ts">
interface Topic {
  name: string
  slug: string
}

interface Instructor {
  image?: string
  name: string
  title: string
  url: string
  bio: string
  topics: Topic[]
}

interface InstructorsFeed {
  instructors?: Instructor[]
  uuid: string
}

// Define props with type and default values using withDefaults
const props = withDefaults(defineProps<InstructorsFeed>(), {
  instructors: () => [],
  uuid: ''
});

const getSize = () => {
  const sizes = document.querySelectorAll(`[data-uuid="${props.uuid}"] [data-id="size"]`)
  let size = 1
  sizes?.forEach((el: HTMLElement) => {
    if (window.getComputedStyle(el).display !== 'none') {
      size = Number(el.dataset.value)
    }
  })
  return size
}

const shouldFadeIn = (index: number) => {
  const size = getSize();
  if (size === 1) {
    if (index > 0) return false;
  } else if (size === 2) {
    if (index > 1) return false;
  } else if (size === 4) {
    if (index > 3) return false;
  }
  return true;
}

const scroller = ref<HTMLElement>();
const scrollPosition = ref(scroller.value?.scrollLeft || 0)
const activeIndex = ref(0)

const focusedIndex = () => {
  // Get the data-index of the card closest to scroller.scrollLeft
  if (!scroller.value) return 1;
  const cards = scroller.value.querySelectorAll('[data-index]');
  let closestIndex = 0;
  let closestDistance = Infinity;
  cards.forEach((card, index) => {
    const distance = Math.abs(card.getBoundingClientRect().left - scroller.value!.getBoundingClientRect().left);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  })
  const size = getSize();
  return Math.ceil(((closestIndex + 1)/size)+(size === 1 ? 0 : 0.1));
}

const scrollMax = () => {
  return scroller.value?.scrollWidth - scroller.value?.clientWidth - 5
}

const scrollLeft = () => {
  const size = getSize()
  activeIndex.value = (activeIndex.value - size) <= 0 ? 0 : (activeIndex.value - size)
  document.querySelector(`[data-uuid="${props.uuid}"] [data-instructor="1"][data-index="${activeIndex.value}"]`)?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'start'
  });
}

const scrollRight = () => {
  const size = getSize()
  activeIndex.value =
    (activeIndex.value + size) >= (props.instructors?.length - size) ?
    (props.instructors?.length - size) || 0 :
    (activeIndex.value + size)
  document.querySelector(`[data-uuid="${props.uuid}"] [data-instructor="1"][data-index="${activeIndex.value}"]`)?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'start'
  });
}

const rootElement = ref(null)
onBeforeMount(() => {
  rootElement.value = (document.querySelector(`[data-uuid="${props.uuid}"]`) as HTMLElement);
  delete rootElement.value.dataset['json']
})
</script>
